/**
 * 消息类型与载荷定义，与 server/pkg/protocol/envelope.go、messages*.go 对应。
 */

import type { WBSnapshot } from './whiteboard-types'

/** WebSocket 文本帧的统一信封。 */
export interface Envelope<T = unknown> {
  /** 消息类型 */
  t: string
  /** 请求序号，服务端在应答里回填 */
  i?: number
  /** 载荷 */
  d?: T
}

/** 消息类型常量。 */
export const MsgType = {
  // 连接与房间
  Hello: 'hello',
  HelloOK: 'hello.ok',
  RoomJoin: 'room.join',
  RoomJoined: 'room.joined',
  RoomLeave: 'room.leave',
  UserJoined: 'user.joined',
  UserLeft: 'user.left',
  RoomPropSet: 'room.prop.set',
  RoomPropChanged: 'room.prop.changed',
  RoomKick: 'room.kick',
  RoomEnd: 'room.end',

  // 等候室
  WaitJoined: 'wait.joined',
  WaitList: 'wait.list',
  WaitAdmit: 'wait.admit',
  WaitReject: 'wait.reject',
  WaitAdmitted: 'wait.admitted',
  WaitRejected: 'wait.rejected',
  WaitMoveTo: 'wait.moveto',

  // 投票
  PollStart: 'poll.start',
  PollStarted: 'poll.started',

  // 课堂签到。与投票分开：签到要落库供教务查，
  // 关心的是「谁没应答」而不是「各选项多少票」。
  AttendStart: 'attend.start',
  AttendStarted: 'attend.started',
  AttendMark: 'attend.mark',
  AttendStat: 'attend.stat',
  AttendEnd: 'attend.end',
  AttendEnded: 'attend.ended',
  PollVote: 'poll.vote',
  PollStat: 'poll.stat',
  PollEnd: 'poll.end',
  PollEnded: 'poll.ended',

  // 分组讨论
  GroupOpen: 'group.open',
  GroupOpened: 'group.opened',
  GroupClose: 'group.close',
  GroupClosed: 'group.closed',
  GroupNotify: 'group.notify',
  GroupNotified: 'group.notified',

  // 实时字幕
  CaptionToggle: 'caption.toggle',
  CaptionState: 'caption.state',
  CaptionText: 'caption.text',

  // 举手
  HandRaise: 'hand.raise',

  // 会中管控
  MeetingControl: 'meeting.control',

  // 用户属性
  UserPropSet: 'user.prop.set',
  UserPropChanged: 'user.prop.changed',
  UserStateSet: 'user.state.set',
  UserStateChanged: 'user.state.changed',
  UserInfoSet: 'user.info.set',
  UserInfoChanged: 'user.info.changed',
  UserRelationSet: 'user.relation.set',
  UserRelationChanged: 'user.relation.changed',

  // 麦序
  MicRequest: 'mic.request',
  MicQueued: 'mic.queued',
  MicAssign: 'mic.assign',
  MicAssigned: 'mic.assigned',
  MicRelease: 'mic.release',
  MicReleased: 'mic.released',
  MicLock: 'mic.lock',
  MicLocked: 'mic.locked',
  MicNumSet: 'mic.num.set',
  MicNumChanged: 'mic.num.changed',

  // 文本与私聊
  TextSend: 'text.send',
  TextRecv: 'text.recv',
  PMInvite: 'pm.invite',
  PMInviteReply: 'pm.invite.reply',
  PMCancel: 'pm.cancel',
  PMStop: 'pm.stop',
  ReactSend: 'react.send',
  ReactRecv: 'react.recv',

  // 媒体
  RTCOffer: 'rtc.offer',
  RTCAnswer: 'rtc.answer',
  RTCCandidate: 'rtc.candidate',
  RTCPublish: 'rtc.publish',
  RTCPublished: 'rtc.published',
  RTCUnpublish: 'rtc.unpublish',
  RTCSubscribe: 'rtc.subscribe',
  RTCUnsubscribe: 'rtc.unsubscribe',
  RTCSpeaking: 'rtc.speaking',

  // 白板
  WBOp: 'wb.op',
  WBSnapshot: 'wb.snapshot',
  WBPageAdd: 'wb.page.add',
  WBPageDel: 'wb.page.del',
  WBPageClear: 'wb.page.clear',
  WBPageTitle: 'wb.page.title',
  WBPageGoto: 'wb.page.goto',
  WBLockPage: 'wb.lock.page',
  WBLockObj: 'wb.lock.obj',
  WBBlobEnd: 'wb.blob.end',

  // 录制
  RecordStart: 'record.start',
  RecordStarted: 'record.started',
  RecordStop: 'record.stop',
  RecordStopped: 'record.stopped',
  RecordTick: 'record.tick',

  // 文件传输
  FileOffer: 'file.offer',
  FileAccept: 'file.accept',
  FileCancel: 'file.cancel',
  FileDone: 'file.done',

  // 应用共享
  ShareStart: 'share.start',
  ShareStarted: 'share.started',
  ShareStop: 'share.stop',
  SharePause: 'share.pause',
  ShareCtrlReq: 'share.ctrl.request',
  ShareCtrlReply: 'share.ctrl.reply',
  ShareCtrlMouse: 'share.ctrl.mouse',
  ShareCtrlKey: 'share.ctrl.key',

  // 通用
  Error: 'error',
  Ping: 'ping',
  Pong: 'pong',
} as const

/** 操作码。 */
export type OpCode = 'add' | 'remove' | 'set'

/** 媒体种类。 */
export type MediaKind = 'audio' | 'video' | 'screen'

/** WebRTC 连接方向。 */
export type RTCKind = 'pub' | 'sub'

// ---------- 载荷类型 ----------

export interface ICEServerConf {
  urls: string[]
  username?: string
  credential?: string
}

export interface HelloOK {
  selfId: number
  serverTime: number
  iceServers: ICEServerConf[]
}

/**
 * 房间内一名用户的公开信息。
 *
 * 权限与状态在线上是**数值**，不是字母串 —— 字母串只覆盖一部分位，
 * 用它当线上格式会静默丢掉 PermAppShare / PermOpWB 等 9 个权限位。
 * 详见 server/pkg/protocol/permission.go 的说明。
 */
export interface UserInfo {
  id: number
  name: string
  info?: string
  prop: number
  state: number
  micId: number
  enterTime: number
}

export interface RoomInfo {
  id: number
  name: string
  flags: number
  micNum: number
  videoNum: number
  maxUsers: number
  notice?: string
  ownerId: number
  /** 当前正在屏幕共享的席位，-2 表示无人共享 */
  sharerId: number
}

/** 表情回应。白名单由服务端把关，这里只负责画出来。 */
export interface ReactRecv {
  fromId: number
  name: string
  emoji: string
  time: number
}

export interface TextRecv {
  fromId: number
  destId: number
  secret: boolean
  broadcast: boolean
  flag?: number
  text: string
  time: number
  name?: string
}

export interface RoomJoined {
  selfId: number
  room: RoomInfo
  users: UserInfo[]
  mics: number[]
  micLocks: boolean[]
  history?: TextRecv[]
  /** 进房时刻的白板全量快照 */
  whiteboard?: WBSnapshot
  serverTime: number
}

export interface UserLeft {
  id: number
  reason: number
  time: number
}

export interface UserPropChanged {
  destId: number
  fromId: number
  prop: number
  final: number
  time: number  /** 变更后的主持人席位（最早的 op）；用来区分主持与联席 */
  ownerId?: number
}

export interface UserStateChanged {
  destId: number
  fromId: number
  state: number
  final: number
  time: number
}

export interface UserInfoChanged {
  destId: number
  fromId: number
  info: string
  time: number
  /** 变更后的显示名；只改简介时服务端也会带上当前名字 */
  name?: string
}

/** 私聊邀请。 */
export interface PMInvite {
  fromId: number
  toId: number
  text?: string
}

/** 私聊邀请的应答。 */
export interface PMInviteReply {
  fromId: number
  toId: number
  agree: boolean
}

/** 私聊取消/结束，两端共用。 */
export interface PMPeer {
  fromId: number
  toId: number
}

/** 用户关系变更通知。 */
export interface UserRelationChanged {
  mainId: number
  destId: number
  relation: number
  time: number
}

export interface RoomPropChanged {
  flags: number
  delta: number
  fromId: number
  opcode: OpCode
  time: number
}

export interface MicQueued {
  id: number
  order: number
}

export interface MicAssigned {
  fromId: number
  destId: number
  micId: number
  time: number
}

export interface MicReleased {
  fromId: number
  destId: number
  micId: number
  time: number
}

export interface MicLocked {
  fromId: number
  micId: number
  lock: boolean
  time: number
}

export interface MicNumChanged {
  num: number
  fromId: number
}

export interface RTCPublished {
  userId: number
  media: MediaKind[]
  trackIds: string[]
}

export interface RTCSpeaking {
  userId: number
  level: number
}

export interface RecordStarted {
  ok: boolean
  recordId?: string
  name?: string
  time: number
  err?: string
}

export interface RecordStopped {
  recordId: string
  size: number
  begin: number
  elapsed: number
  url?: string
}

export interface RecordTick {
  elapsed: number
  remaining: number
}

export interface FileOffer {
  fileId: string
  fromId: number
  destId: number
  name: string
  size: number
}

export interface FileDone {
  fileId: string
  url: string
}

export interface ShareStarted {
  userId: number
  mode: 'screen' | 'window'
}

/** 远程控制的鼠标事件。坐标为共享画面的归一化比例 0-1。 */
export interface ShareCtrlMouse {
  x: number
  y: number
  button: number
  /** down / up / move / wheel */
  action: string
  delta?: number
}

/** 远程控制的键盘事件。 */
export interface ShareCtrlKey {
  /** W3C KeyboardEvent.code */
  code: string
  /** down / up */
  action: string
  /** 1=shift 2=ctrl 4=alt 8=meta */
  mods: number
}

export interface ProtoError {
  code: string
  msg: string
}

/** 错误码，与 server/pkg/protocol/errors.go 对应。 */
export const ErrCode = {
  AuthRequired: 'AUTH_REQUIRED',
  AuthFailed: 'AUTH_FAILED',
  RoomNotFound: 'ROOM_NOT_FOUND',
  RoomFull: 'ROOM_FULL',
  RoomLocked: 'ROOM_LOCKED',
  RoomPassword: 'ROOM_PASSWORD',
  PermDenied: 'PERM_DENIED',
  MicFull: 'MIC_FULL',
  MicLocked: 'MIC_LOCKED',
  RateLimited: 'RATE_LIMITED',
  PayloadTooLarge: 'PAYLOAD_TOO_LARGE',
  BadRequest: 'BAD_REQUEST',
  Internal: 'INTERNAL',
  NotInRoom: 'NOT_IN_ROOM',
  AlreadyInRoom: 'ALREADY_IN_ROOM',
  TargetNotFound: 'TARGET_NOT_FOUND',
  NotSupported: 'NOT_SUPPORTED',
  Conflict: 'CONFLICT',
} as const

/** 二进制帧的 kind 取值。 */
export const BinKind = {
  WhiteboardBlob: 1,
  FileChunk: 2,
  AppShareFrame: 3,
} as const

export const BIN_FRAME_VERSION = 1

/**
 * 把服务端错误翻译成课堂里说得通的一句话。
 *
 * 服务端的 msg 是从服务端视角写的（「权限不足」「麦位已被锁定」），
 * 直接甩给学生看，他既不知道自己缺哪个权限，也不知道下一步该干嘛。
 * 这里按错误码补上「所以你该怎么办」。
 *
 * 返回 null 表示这条错误不必打扰用户 —— 比如重复进房，界面上什么都没坏。
 */
export function explainError(code: string, msg: string): string | null {
  switch (code) {
    case ErrCode.RoomPassword:
      return '进房口令不对，请向老师或助教确认。'
    case ErrCode.RoomFull:
      return '教室人数已满，等有人离开后再试。'
    case ErrCode.RoomLocked:
      return '这间教室已锁定或尚未开放。'
    case ErrCode.RoomNotFound:
      return '找不到这间教室，请核对房间号。'
    case ErrCode.MicFull:
      return '麦位都占满了，已排队等候。'
    case ErrCode.MicLocked:
      return '这个麦位被老师锁住了。'
    case ErrCode.PermDenied:
      // 服务端会说明具体缺什么，保留原文更有信息量。
      return `${msg}。如需要请向老师申请。`
    case ErrCode.RateLimited:
      return '操作太快了，缓一缓再试。'
    case ErrCode.PayloadTooLarge:
      return `${msg}。请分次发送或缩小内容。`
    case ErrCode.AuthRequired:
    case ErrCode.AuthFailed:
      return '登录状态已失效，请重新登录。'
    case ErrCode.NotInRoom:
      return '尚未进入教室，请先进入。'
    case ErrCode.AlreadyInRoom:
      // 多半是重连时重复进房，界面上什么都没坏，不必打扰用户。
      return null
    case ErrCode.TargetNotFound:
      return '对方已经离开教室了。'
    case ErrCode.NotSupported:
      return `${msg}（当前教室未开放该功能）`
    case ErrCode.Internal:
      return '服务端出了点问题，稍后再试；如果一直这样请联系管理员。'
    default:
      return msg || '操作失败'
  }
}

// ---------- 等候室 ----------

/** 候场名单里的一条。 */
export interface WaitEntry {
  waitId: number
  account: string
  name: string
  since: number
}

/** 「你在等候室里」。 */
export interface WaitJoined {
  waitId: number
  roomId: number
  name: string
  position: number
  time: number
}

/** 发给主持人的候场名单（全量）。 */
export interface WaitList {
  entries: WaitEntry[]
  time: number
}

/** 已被准入，可以重发 room.join 了。 */
export interface WaitAdmitted {
  roomId: number
  time: number
}

/** 被拒绝进入。 */
export interface WaitRejected {
  reason?: string
  time: number
}

// ---------- 投票 ----------

/** 投票的规模上限，与服务端 MaxPoll* 对应。 */
export const PollLimits = {
  MaxOptions: 10,
  MaxTitleLen: 120,
  MaxOptLen: 60,
} as const

export interface PollOption {
  index: number
  text: string
}

/** 投票开始。 */
export interface PollStarted {
  pollId: number
  title: string
  options: PollOption[]
  multi?: boolean
  anonymous?: boolean
  liveStat?: boolean
  fromId: number
  time: number
}

/** 一个选项的票数。匿名投票时 voters 为空。 */
export interface PollCount {
  index: number
  count: number
  voters?: number[]
}

/** 实时统计。 */
export interface PollStat {
  pollId: number
  counts: PollCount[]
  voted: number
  total: number
  time: number
}

/** 最终结果。 */
export interface PollEnded {
  pollId: number
  title: string
  /** 选项文本。中途进房的人没收到过 poll.started，只有下标读不出题面 */
  options: PollOption[]
  counts: PollCount[]
  voted: number
  total: number
  time: number
}

// ---------- 分组讨论 ----------

/** 一次分组最多几组，与服务端 MaxGroups 对应。 */
export const MaxGroups = 8

/** 一个小组的编排。 */
export interface GroupSpec {
  index: number
  name: string
  /** 分到该组的账号。用账号而非席位号：换房间后席位会重新分配 */
  members: string[]
  /** 该组的子房间号，由服务端分配 */
  roomId: number
}

/** 分组已开启。 */
export interface GroupOpened {
  groups: GroupSpec[]
  fromId: number
  time: number
}

/** 分组结束，请回主房间。 */
export interface GroupClosed {
  mainRoomId: number
  time: number
}

/** 主持人给各组的通知。 */
export interface GroupNotified {
  text: string
  time: number
}

// ---------- 实时字幕 ----------

/** 字幕音频块的二进制帧类型，与服务端 BinKindCaptionAudio 一致。 */
export const BIN_KIND_CAPTION_AUDIO = 4

/** 字幕开关状态。 */
export interface CaptionState {
  on: boolean
  fromId: number
  time: number
}

/** 一句字幕。 */
export interface CaptionText {
  fromId: number
  name: string
  text: string
  /** 为假是中间结果，客户端应就地替换上一条 */
  final: boolean
  time: number
}

/** 一个人的签到情况。 */
export interface AttendOne {
  userId: number
  account: string
  nick: string
  /** 'in' 准时；'late' 限时后补签 */
  status?: 'in' | 'late'
  /** 应答时刻（秒）。没签到的人为 0 */
  at?: number
}

export interface AttendStarted {
  roundId: number
  limitS?: number
  fromId: number
  /** 发起那一刻的在场学员数，用作签到率的分母 */
  expected: number
  time: number
}

export interface AttendStat {
  roundId: number
  marked: number
  total: number
  time: number
}

export interface AttendEnded {
  roundId: number
  marked: AttendOne[]
  missing: AttendOne[]
  total: number
  time: number
}
